import { useEffect, useRef, useState } from "react"

/**
 * Erkennt, dass eine neue Fassung des Portals ausgeliefert wurde.
 *
 * 🔴 Anlass 04.09.2026, nach dem vierten „Fehler: Missing queryFn" in vier
 * Wochen. Der Fehler war jedes Mal schon behoben, als er gemeldet wurde — nur
 * lief im Browser noch die alte Fassung:
 *
 *   Sanitec bekam am 04.09. bis 09:26 sieben Mails. Die Liste in der Meldung
 *   von 10:01 zeigte keine davon; ihre oberste Zeile stammte vom Vorabend
 *   20:33. Der Tab stand seit dem Vorabend offen und hatte nie neu geladen.
 *
 * 🔑 Die Auslieferung war nie das Problem: `index.html` kommt mit `no-cache`,
 * die Bundles tragen einen Hash und liegen `immutable`. Ein **Neuladen** holt
 * die neue Fassung sofort. Nur tut eine offene Einzelseiten-Anwendung das von
 * sich aus nie — sie laeuft weiter, bis jemand F5 drueckt. Bei einem Postfach,
 * das man morgens oeffnet und abends zuklappt, sind das Tage.
 *
 * Damit ist jeder Fix erst dann beim Bedienenden, wenn dieser zufaellig neu
 * laedt. Genau das hat drei Fehlermeldungen, eine falsche Rueckfall-Diagnose
 * und einen halben Vormittag Suche gekostet.
 *
 * Dieser Haken vergleicht den Namen des Einstiegs-Bundles aus `index.html` mit
 * dem, der beim Laden dieser Seite galt. Weichen sie ab, ist eine neue Fassung
 * da.
 *
 * 🔑 Bewusst KEIN automatisches Neuladen: im Portal haengen offene Entwuerfe
 * und halb ausgefuellte Dialoge daran. Der Bedienende entscheidet, wann.
 */

/** `/assets/index-ClmC23YV.js` → der Name, der sich bei jedem Build aendert. */
const ENTRY_MUSTER = /assets\/(index-[A-Za-z0-9_-]+\.js)/

/**
 * Der Einstieg, mit dem DIESE Seite geladen wurde.
 *
 * `import.meta.url` ist die Adresse des Moduls, in dem dieser Code steht —
 * im Build ist das der Einstiegs-Chunk. Passt sie nicht auf das Muster (etwa
 * im Entwicklungsserver oder bei anderem Code-Splitting), bleibt der Wert
 * offen und wird beim ersten Abruf nachgeholt.
 */
function eigenerEinstieg(): string | null {
  try {
    return ENTRY_MUSTER.exec(import.meta.url)?.[1] ?? null
  } catch {
    return null
  }
}

async function ausgelieferterEinstieg(): Promise<string | null> {
  try {
    // `no-store`, sonst beantwortet der Browser die Frage aus seinem eigenen
    // Speicher — und die lautet dann immer „unveraendert".
    const r = await fetch("/", { cache: "no-store", credentials: "include" })
    if (!r.ok) return null
    return ENTRY_MUSTER.exec(await r.text())?.[1] ?? null
  } catch {
    // Kein Netz, Deploy gerade unterwegs, Sitzung abgelaufen: das ist keine
    // neue Fassung. Im Zweifel nichts behaupten.
    return null
  }
}

const ABSTAND_MS = 5 * 60 * 1000

export function useNeueFassung(): boolean {
  const [neu, setNeu] = useState(false)
  const eigener = useRef<string | null>(eigenerEinstieg())

  useEffect(() => {
    let gestoppt = false

    async function pruefen() {
      if (gestoppt || neu) return
      const ausgeliefert = await ausgelieferterEinstieg()
      if (gestoppt || !ausgeliefert) return
      if (!eigener.current) {
        // Erster Abruf ohne bekannten eigenen Stand: als Bezugspunkt merken.
        eigener.current = ausgeliefert
        return
      }
      if (ausgeliefert !== eigener.current) setNeu(true)
    }

    pruefen()
    const timer = window.setInterval(pruefen, ABSTAND_MS)
    // Der wichtigste Zeitpunkt: jemand kommt nach Stunden zum Tab zurueck.
    const beiRueckkehr = () => { if (document.visibilityState === "visible") pruefen() }
    document.addEventListener("visibilitychange", beiRueckkehr)
    window.addEventListener("focus", beiRueckkehr)

    return () => {
      gestoppt = true
      window.clearInterval(timer)
      document.removeEventListener("visibilitychange", beiRueckkehr)
      window.removeEventListener("focus", beiRueckkehr)
    }
  }, [neu])

  return neu
}
